<i18n src="./resources/locales.json"></i18n>

<template>
  <div class="bp-active-monitoring-card">
    <div class="card-header">
      <div class="card-title">
        <v-btn
          v-show="!isDefaultTabActive"
          class="card-header-action back-btn"
          icon
          x-small
          @click="showDefaultContent"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        {{ businessProcess.businessProcessName }}
        <v-tooltip color="surface lighten-2" right>
          <template v-slot:activator="{ on }">
            <v-btn
              v-show="isDefaultTabActive"
              class="card-header-action pin"
              icon
              x-small
              :class="{ active: pinned }"
              v-on="on"
              @click="togglePin"
            >
              <v-icon>{{ pinIcon }}</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(pinned ? 'unpin' : 'pin') }}</span>
        </v-tooltip>
      </div>

      <v-tabs
        ref="cardNavigationTabs"
        v-model="tabs.current"
        height="24"
        background-color="transparent"
        class="card-navigation-toolbar"
      >
        <v-tab
          v-for="(option, index) in options"
          v-show="index !== tabs.default"
          :key="index"
          class="navigation-tab"
          @click="setCurrentTab(index)"
        >
          <v-tooltip bottom color="secondary">
            <template v-slot:activator="{ on }">
              <v-icon class="navigation-icon" v-on="on">{{ option.icon }}</v-icon>
            </template>
            <span v-once>{{ $t(option.titleKeyLabel) }}</span>
          </v-tooltip>
        </v-tab>
      </v-tabs>
    </div>

    <div class="card-content">
      <transition name="fade" mode="out-in">
        <keep-alive>
          <Component :is="currentContent" :business-process-id="businessProcessId" :data="tabs.data" />
        </keep-alive>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import EcsBusinessProcessMonitoringOrganizedDataCard from '@/components/ecs-business-process-monitoring-card/EcsBusinessProcessMonitoringOrganizedDataCard';
import EcsBusinessProcessMonitoringAgentsCard from '@/components/ecs-business-process-monitoring-card/EcsBusinessProcessMonitoringAgentsCard';
import EcsBusinessProcessMonitoringCallbacksCard from '@/components/ecs-business-process-monitoring-card/EcsBusinessProcessMonitoringCallbacksCard';
import EcsBusinessProcessMonitoringThresholdsCard from '@/components/ecs-business-process-monitoring-card/EcsBusinessProcessMonitoringThresholdsCard';

export default {
  name: 'EcsBusinessProcessActiveMonitoringCard',

  components: {
    EcsBusinessProcessMonitoringOrganizedDataCard,
    EcsBusinessProcessMonitoringAgentsCard,
    EcsBusinessProcessMonitoringCallbacksCard,
    EcsBusinessProcessMonitoringThresholdsCard
  },

  props: {
    businessProcessId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      tabs: {
        current: 3,
        default: 3,
        data: {}
      },
      pinned: false
    };
  },

  computed: {
    ...mapGetters('BUSINESS_PROCESSES', ['supervisedBusinessProcesses']),

    ...mapGetters('SETTINGS', ['supervisorDashboardSettings']),

    businessProcess() {
      return this.supervisedBusinessProcesses.find(
        businessProcess => businessProcess.businessProcessId === this.businessProcessId
      );
    },

    isDefaultTabActive() {
      return this.tabs.current === this.tabs.default;
    },

    options() {
      return [
        {
          icon: 'mdi-account',
          component: 'EcsBusinessProcessMonitoringAgentsCard',
          titleKeyLabel: 'agentsCard.tabs.agents'
        },
        {
          icon: 'mdi-calendar-text',
          component: 'EcsBusinessProcessMonitoringCallbacksCard',
          titleKeyLabel: 'agentsCard.tabs.callbacksLink'
        },
        {
          icon: 'mdi-tune',
          component: 'EcsBusinessProcessMonitoringThresholdsCard',
          titleKeyLabel: 'agentsCard.tabs.thresholds'
        },
        {
          icon: 'mdi-application',
          component: 'EcsBusinessProcessMonitoringOrganizedDataCard',
          titleKeyLabel: ''
        }
      ];
    },

    currentContent() {
      return this.options[this.tabs.current].component;
    },

    dashboardSettings() {
      return this.supervisorDashboardSettings.adaptiveDashboards.cards.find(
        settings => settings.businessProcessId === this.businessProcessId
      );
    },

    pinIcon() {
      return this.pinned ? 'mdi-pin' : 'mdi-pin-outline';
    }
  },

  mounted() {
    if (this.dashboardSettings) {
      this.tabs.current = this.dashboardSettings.selectedCardIndex;
      this.pinned = this.dashboardSettings.isPinned;
    }
  },

  methods: {
    ...mapActions('SETTINGS', ['updateSupervisorAdaptiveCardSettings']),

    showDefaultContent() {
      this.setCurrentTab(this.tabs.default);
    },

    setCurrentTab(index, tabData) {
      this.updateDashboard(index, index !== this.tabs.default, tabData);
    },

    togglePin() {
      this.updateDashboard(this.tabs.current, !this.pinned);
    },

    updateDashboard(selectedCardIndex, isPinned, tabData = {}) {
      this.tabs.current = selectedCardIndex;
      this.pinned = isPinned;
      this.tabs.data = tabData;

      this.updateSupervisorAdaptiveCardSettings({
        businessProcessId: this.businessProcessId,
        selectedCardIndex,
        isPinned
      });
    }
  }
};
</script>

<style scoped>
.bp-active-monitoring-card {
  display: grid;
  grid-template-rows: 42px calc(100% - 42px);
  width: 100%;
  background-color: var(--v-surface-lighten1);
  border-radius: 8px;
}

.card-header {
  display: grid;
  height: 42px;
  grid-template-columns: auto min-content;
}

.card-title {
  font-size: 24px;
  height: 42px;
  line-height: 42px;
  white-space: nowrap;
  padding: 0 0 0 8px;
}

.card-header-action {
  width: 32px;
  height: 32px;
  line-height: 32px;
  text-align: center;
  margin: 5px;
  opacity: 0.75;
}

.card-header-action.back-btn {
  margin: -2px 0 0 0;
}

.card-header-action.pin:not(.active) {
  transform: translateY(-2px) rotate(16deg);
}

.card-navigation-toolbar {
  justify-self: end;
  align-self: center;
  width: fit-content;
  padding-right: 8px;
}

.navigation-tab.v-tab {
  min-width: 24px;
  padding: 0;
}

.navigation-icon {
  font-size: 18px;
}

.card-content {
  height: 100%;
}
</style>
